import type { PezEffectDefApi } from '@/api/types/photo-effects'

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function resolveUiZero(def: PezEffectDefApi, uiLo: number, uiHi: number): number {
  if (typeof def.uiZero === 'number' && Number.isFinite(def.uiZero)) return def.uiZero

  if (typeof def.uiDefault === 'number' && Number.isFinite(def.uiDefault)) return def.uiDefault

  return uiLo <= 0 && uiHi >= 0 ? 0 : uiLo
}

function resolveRangeZero(def: PezEffectDefApi, rangeLo: number, rangeHi: number): number {
  if (typeof def.rangeZero === 'number' && Number.isFinite(def.rangeZero)) return def.rangeZero

  return rangeLo < 0 && rangeHi > 0 ? 0 : rangeLo
}

/** True when UI/backend neutral is the midpoint between asymmetric min/max (e.g. Brightness, Gamma). */
function usesPiecewiseNeutralMapping(
  def: PezEffectDefApi,
  uiLo: number,
  uiHi: number,
  rangeLo: number,
  rangeHi: number
): boolean {
  const uiZero = resolveUiZero(def, uiLo, uiHi)
  const rangeZero = resolveRangeZero(def, rangeLo, rangeHi)

  return uiLo < uiZero && uiHi > uiZero && rangeLo < rangeZero && rangeHi > rangeZero
}

function lerp(a: number, b: number, t: number): number {
  return a + t * (b - a)
}

function mapSegment(
  value: number,
  segLo: number,
  segHi: number,
  outLo: number,
  outHi: number
): number {
  if (segHi === segLo) return outLo

  const t = (value - segLo) / (segHi - segLo)
  const mapped = lerp(outLo, outHi, t)

  return Number.isFinite(mapped) ? mapped : outLo
}

/**
 * Map UI slider value (uiLo…uiHi) → backend request value (rangeLo…rangeHi).
 * Symmetric -10…10 effects use a piecewise map through UI/backend zero so "0, neutral" sends `v=0`
 * even when rangeLo/rangeHi are asymmetric (WMPCM-9494 AC 7 / 9).
 */
export function mapPhotoEffectUiToBackendValue(
  uiValue: number,
  def: PezEffectDefApi | undefined
): number {
  if (!def) return uiValue

  const uiLo = finiteOr(def.uiLo, uiValue)
  const uiHi = finiteOr(def.uiHi, uiValue)
  const rangeLo = finiteOr(def.rangeLo, uiValue)
  const rangeHi = finiteOr(def.rangeHi, uiValue)

  if (uiHi === uiLo) return rangeLo

  if (usesPiecewiseNeutralMapping(def, uiLo, uiHi, rangeLo, rangeHi)) {
    const uiZero = resolveUiZero(def, uiLo, uiHi)
    const rangeZero = resolveRangeZero(def, rangeLo, rangeHi)

    if (uiValue === uiZero) return rangeZero
    if (uiValue < uiZero) return mapSegment(uiValue, uiLo, uiZero, rangeLo, rangeZero)

    return mapSegment(uiValue, uiZero, uiHi, rangeZero, rangeHi)
  }

  return mapSegment(uiValue, uiLo, uiHi, rangeLo, rangeHi)
}

/** Inverse of {@link mapPhotoEffectUiToBackendValue} for GET /photos/pez restore. */
export function mapPhotoEffectBackendToUiValue(
  backendValue: number,
  def: PezEffectDefApi | undefined
): number {
  if (!def) return backendValue

  const uiLo = finiteOr(def.uiLo, backendValue)
  const uiHi = finiteOr(def.uiHi, backendValue)
  const rangeLo = finiteOr(def.rangeLo, backendValue)
  const rangeHi = finiteOr(def.rangeHi, backendValue)

  if (rangeHi === rangeLo) return uiLo

  if (usesPiecewiseNeutralMapping(def, uiLo, uiHi, rangeLo, rangeHi)) {
    const uiZero = resolveUiZero(def, uiLo, uiHi)
    const rangeZero = resolveRangeZero(def, rangeLo, rangeHi)

    if (backendValue === rangeZero) return uiZero
    if (backendValue < rangeZero) return mapSegment(backendValue, rangeLo, rangeZero, uiLo, uiZero)

    return mapSegment(backendValue, rangeZero, rangeHi, uiZero, uiHi)
  }

  return mapSegment(backendValue, rangeLo, rangeHi, uiLo, uiHi)
}

/** Format a backend `v` for POST (Core 3 stores strings). */
export function formatPezBackendValue(value: number): string {
  if (!Number.isFinite(value)) return '0'

  const rounded = Math.round(value * 1_000_000) / 1_000_000

  return String(rounded)
}

/**
 * Sepia POST `v`: whole backend integer in catalog range (typically 50…100).
 * UI stays 1…100 per WMPCM-9477; backend rejects UI-scale values (e.g. v below 50).
 */
export function formatSepiaPezBackendValue(
  uiValue: number,
  def: PezEffectDefApi | undefined
): string {
  const backend = mapPhotoEffectUiToBackendValue(uiValue, def)

  if (!Number.isFinite(backend)) return '50'

  return String(Math.round(backend))
}

export function parsePezBackendValue(raw: string | number | undefined): number | null {
  if (raw == null || raw === '') return null

  const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw).trim())

  return Number.isFinite(n) ? n : null
}
